import React, { useState, useEffect, useCallback } from "react";
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "./firebaseConfig";
import LandingPage from './components/LandingPage';
import LoadingScreen from './components/LoadingScreen';
import HomePage from './components/HomePage';
import OnboardingScreen from "./OnboardingScreen";
import AccountChoice from "./AccountChoice";

// Pages
import Login from "./Login.js";
import Dashboard from "./Dashboard";
import Signup from "./Signup";
import ManageUsers from "./ManageUsers";
import CreateQuestions from "./CreateQuestions";
import EditQuestion from "./EditQuestion";
import CreateQuiz from "./CreateQuiz";
import ViewQuizzes from "./ViewQuizzes";
import QuizSuccess from "./QuizSuccess";
import EnterQuiz from "./EnterQuiz";
import ViewQuizResponses from "./ViewQuizResponses"; // For admin/teacher
import ViewMyScores from "./ViewMyScores";          // NEW COMPONENT for students
import MyAssessments from "./MyAssessments";
import Organisations from "./Organisations";
import OrganisationLibrary from "./OrganisationLibrary";
import StudentLearningContent from "./StudentLearningContent";
import TypingTest from "./TypingTest";              // NEW COMPONENT for speed typing test
import QuizResults from "./QuizResults";            // NEW COMPONENT for viewing quiz results
import JoinTeacher from "./joinTeacher";            // NEW COMPONENT for students to join a teacher
import ViewStudents from "./ViewStudents";          // NEW COMPONENT for teachers to view their students
import StudentProfile from "./StudentProfile";      // NEW COMPONENT for viewing detailed student analytics
import AutoReport from "./AutoReport";              // NEW COMPONENT for generating student reports
import PaperSubmission from "./PaperSubmission";    // NEW COMPONENT for paper submission
import GenerateQuizQuestions from "./GenerateQuizQuestions"; // NEW COMPONENT for AI question generation
import MyClasses from "./MyClasses";                // NEW COMPONENT for students to view their classes
import MobilePaperUpload from "./MobilePaperUpload"; // QR Upload Phase 1
import EditQuiz from "./EditQuiz";                   // Edit Quiz page
import ChangePasswordRequired from "./ChangePasswordRequired";
import AccountSecurity from "./AccountSecurity";
import { ensureTeacherClaim } from "./utils/teacherClaim";
import {
  normalizeAccountFlags,
  requiresForcedPasswordChange,
} from "./utils/forcedPasswordChange";

const AUTH_ENTRY_PATHS = ["/login", "/signup"];

// An entry confirmation lasts only for the redirect out of /login or /signup,
// so a later explicit "Log in" click asks again rather than entering silently.
const AuthEntryConfirmationReset = ({ confirmed, onReset }) => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (confirmed && !AUTH_ENTRY_PATHS.includes(pathname)) onReset();
  }, [confirmed, pathname, onReset]);
  return null;
};

const App = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // uid of an account the person at this browser has just chosen to enter:
  // signed in or registered on this page, or picked via "Continue as".
  const [confirmedEntryUid, setConfirmedEntryUid] = useState(null);

  useEffect(() => {
    const auth = getAuth();
    let unsubscribeProfile = () => {};
    let initialAuthResolved = false;
    let authGeneration = 0;

    // Monitor authentication state
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      unsubscribeProfile();
      unsubscribeProfile = () => {};
      authGeneration += 1;
      const generation = authGeneration;
      // Profile results from a previous auth state (e.g. the account that was
      // just switched away from) must never repopulate the current user.
      const isCurrent = () => generation === authGeneration;
      setLoading(true);
      setError(null);

      // The first auth result is a session restored from persistence, created by
      // whoever last used this browser. Only later sign-ins happened on this page.
      const isRestoredSession = !initialAuthResolved;
      initialAuthResolved = true;
      setConfirmedEntryUid(currentUser && !isRestoredSession ? currentUser.uid : null);

      if (currentUser) {
        const userDocRef = doc(db, "users", currentUser.uid);
        unsubscribeProfile = onSnapshot(userDocRef, (userSnap) => {
          if (!isCurrent()) return;
          // Missing profiles remain role-less in memory and are routed to
          // onboarding. Initial profile creation belongs to the explicit
          // signup flows or the backend onboarding fallback.
          const userData = userSnap.exists()
            ? userSnap.data()
            : {
                email: currentUser.email,
                username: currentUser.displayName || "New User",
                displayName: currentUser.displayName || "New User",
                extraTimePercentage: 0,
              };

          // Determine whether onboarding is effectively complete.
          // Any user who already has a role (teacher, admin, or student) bypasses
          // onboarding — this prevents existing accounts from being interrupted.
          // Brand-new users with no role at all must complete onboarding.
          const effectivelyCompleted =
            userData.onboardingCompleted === true ||
            userData.role === "teacher" ||
            userData.role === "admin" ||
            userData.role === "student";

          const accountFlags = normalizeAccountFlags(userData);

          const showProfile = () => {
            if (!isCurrent()) return;
            setUser({
              ...currentUser,
              ...userData,
              uid: currentUser.uid,
              email: userData.email || currentUser.email || "",
              displayName: userData.displayName || currentUser.displayName || "",
              role: userData.role || null,
              onboardingCompleted: effectivelyCompleted,
              ...accountFlags,
            });
            setLoading(false);
          };

          // Teacher pages read Firestore directly, and the rules need the teacher
          // claim on the token. Refresh it before those pages load.
          if (userData.role === "teacher") {
            ensureTeacherClaim(currentUser).then((status) => {
              if (status === "missing") {
                console.error("This teacher account's sign-in token is missing teacher permissions; teacher data may not load.");
              }
              showProfile();
            });
          } else {
            showProfile();
          }
        }, (err) => {
          if (!isCurrent()) return;
          console.error("Error fetching user profile:", err);
          setError("An error occurred while fetching your profile.");
          setLoading(false);
        });
      } else {
        setUser(null);
        setLoading(false);
      }
    });

    return () => {
      authGeneration += 1;
      unsubscribeProfile();
      unsubscribe();
    };
  }, []);

  const clearEntryConfirmation = useCallback(() => setConfirmedEntryUid(null), []);

  if (loading) {
    return <LoadingScreen />;
  }

  if (error) {
    return (
      <div
        role="alert"
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: "1rem",
          height: "100vh",
          fontFamily: "Arial, sans-serif",
          color: "red",
        }}
      >
        <h2>{error}</h2>
        {/* The profile listener has stopped; reloading restarts initialization. */}
        <button type="button" onClick={() => window.location.reload()}>
          Try again
        </button>
      </div>
    );
  }

  const requiresPasswordChange = requiresForcedPasswordChange(user);

  const postLoginDestination = !user
    ? null
    : !user.onboardingCompleted
      ? "/onboarding"
      : requiresPasswordChange
        ? "/change-password-required"
        : "/dashboard";

  const renderProtectedRoute = (isAllowed, element, fallback = "/dashboard") => {
    if (!user) {
      return <Navigate to="/" replace />;
    }

    if (!user.onboardingCompleted) {
      return <Navigate to="/onboarding" replace />;
    }

    if (requiresPasswordChange) {
      return <Navigate to="/change-password-required" replace />;
    }

    return isAllowed ? element : <Navigate to={fallback} replace />;
  };

  // Explicitly opening /login or /signup with an existing session asks which
  // account to use instead of silently entering it. Sessions created on this
  // page (sign-in, registration) keep redirecting straight to their destination.
  const renderAuthEntryRoute = (form) => {
    if (!user) return form;
    if (confirmedEntryUid === user.uid) return <Navigate to={postLoginDestination} replace />;
    return <AccountChoice account={user} onContinue={() => setConfirmedEntryUid(user.uid)} />;
  };

  return (
    <Router>
      <AuthEntryConfirmationReset
        confirmed={confirmedEntryUid !== null}
        onReset={clearEntryConfirmation}
      />
      <Routes>

        {/* ── Public routes ─────────────────────────────────────────────────── */}
        <Route path="/" element={<LandingPage />} />
        <Route
          path="/login"
          element={renderAuthEntryRoute(<Login />)}
        />
        {/* Account creation signs the new user in on this page; like /login, the
            authoritative profile state then chooses the destination (onboarding
            for new accounts). */}
        <Route
          path="/signup"
          element={renderAuthEntryRoute(<Signup />)}
        />

        {/* QR Upload — public, no auth required (scanned on mobile) */}
        <Route
          path="/mobile-paper-upload/:sessionId"
          element={<MobilePaperUpload />}
        />

        {/* ── Onboarding route ──────────────────────────────────────────────── */}
        {/* Show onboarding only for authenticated users who have not yet chosen a role.
            If they are already completed, redirect them straight to the dashboard.
            If they are not logged in at all, send them to login. */}
        <Route
          path="/onboarding"
          element={
            !user
              ? <Navigate to="/login" />
              : user.onboardingCompleted
                ? <Navigate to="/dashboard" />
                : <OnboardingScreen user={user} setUser={setUser} />
          }
        />

        {/* ── Protected routes ──────────────────────────────────────────────── */}
        {/* Each protected route checks:
            1. Is the user authenticated?
            2. Has the user completed onboarding? (if not → /onboarding)
            3. Does the user have the required role? (if not → /dashboard or /)
        */}

        <Route
          path="/change-password-required"
          element={
            !user
              ? <Navigate to="/login" replace />
              : !user.onboardingCompleted
                ? <Navigate to="/onboarding" replace />
                : requiresPasswordChange
                  ? <ChangePasswordRequired user={user} setUser={setUser} />
                  : <Navigate to="/dashboard" replace />
          }
        />

        <Route
          path="/dashboard"
          element={renderProtectedRoute(true, <Dashboard user={user} />)}
        />

        <Route
          path="/organisations"
          element={renderProtectedRoute(user?.role === "teacher", <Organisations user={user} />)}
        />
        <Route
          path="/organisations/:organisationId"
          element={renderProtectedRoute(user?.role === "teacher", <Organisations user={user} />)}
        />
        <Route
          path="/organisations/:organisationId/library"
          element={renderProtectedRoute(user?.role === "teacher", <OrganisationLibrary user={user} />)}
        />
        <Route
          path="/organisations/:organisationId/library/:quizId"
          element={renderProtectedRoute(user?.role === "teacher", <OrganisationLibrary user={user} />)}
        />

        <Route
          path="/account-security"
          element={renderProtectedRoute(true, <AccountSecurity user={user} />)}
        />

        <Route
          path="/manage-users"
          element={renderProtectedRoute(user?.role === "admin", <ManageUsers user={user} />, "/")}
        />

        <Route
          path="/create-questions"
          element={renderProtectedRoute(user && (user.role === "admin" || user.role === "teacher"), <CreateQuestions user={user} />)}
        />

        <Route
          path="/edit-question/:questionId"
          element={renderProtectedRoute(user && (user.role === "admin" || user.role === "teacher"), <EditQuestion user={user} />)}
        />

        <Route
          path="/create-quiz"
          element={renderProtectedRoute(user && (user.role === "admin" || user.role === "teacher"), <CreateQuiz user={user} />)}
        />

        <Route
          path="/view-quiz-responses"
          element={renderProtectedRoute(user && (user.role === "admin" || user.role === "teacher"), <ViewQuizResponses user={user} />)}
        />

        <Route
          path="/view-quizzes"
          element={renderProtectedRoute(user && (user.role === "admin" || user.role === "teacher"), <ViewQuizzes user={user} />)}
        />

        <Route
          path="/quiz-results/:quizId"
          element={renderProtectedRoute(user && (user.role === "admin" || user.role === "teacher"), <QuizResults user={user} />)}
        />

        <Route
          path="/quiz-success"
          element={renderProtectedRoute(user && (user.role === "admin" || user.role === "teacher"), <QuizSuccess user={user} />)}
        />

        <Route
          path="/enter-quiz"
          element={renderProtectedRoute(user?.role === "student", <EnterQuiz user={user} />)}
        />

        <Route
          path="/view-my-scores"
          element={renderProtectedRoute(user?.role === "student", <ViewMyScores user={user} />)}
        />

        <Route
          path="/my-assessments"
          element={renderProtectedRoute(user?.role === "student", <MyAssessments user={user} />)}
        />

        <Route
          path="/learning-content"
          element={renderProtectedRoute(user?.role === "student", <StudentLearningContent user={user} />)}
        />

        <Route
          path="/typing-test"
          element={renderProtectedRoute(user && (user.role === "student" || user.role === "teacher"), <TypingTest user={user} />)}
        />

        <Route
          path="/join-teacher"
          element={renderProtectedRoute(user?.role === "student", <JoinTeacher user={user} />)}
        />

        <Route
          path="/view-students"
          element={renderProtectedRoute(user?.role === "teacher", <ViewStudents user={user} />)}
        />

        <Route
          path="/student-profile/:studentId"
          element={renderProtectedRoute(user && (user.role === "teacher" || user.role === "admin"), <StudentProfile user={user} />)}
        />

        <Route
          path="/auto-report"
          element={renderProtectedRoute(user && (user.role === "admin" || user.role === "teacher"), <AutoReport user={user} />)}
        />

        <Route
          path="/paper-submission"
          element={renderProtectedRoute(user && (user.role === "admin" || user.role === "teacher"), <PaperSubmission user={user} />)}
        />

        <Route
          path="/generate-quiz-questions"
          element={renderProtectedRoute(user && (user.role === "admin" || user.role === "teacher"), <GenerateQuizQuestions user={user} />)}
        />

        <Route
          path="/my-classes"
          element={renderProtectedRoute(user?.role === "student", <MyClasses user={user} />)}
        />

        {/* Edit Quiz — teacher/admin only */}
        <Route
          path="/edit-quiz/:quizId"
          element={renderProtectedRoute(user && (user.role === "admin" || user.role === "teacher"), <EditQuiz user={user} />)}
        />

      </Routes>
    </Router>
  );
};

export default App;
